import { ApiFunctions } from 'hydra-data-types';
import { stripUndefined } from 'ts-utils';
import { ApiClient } from '../../../api/ApiClient';
import { getUtmParams } from '../../../config/AssessmentUtils';
import { recordError } from '../../../util/Errors';
import {
  shouldStartGrantUs65PlusAccess,
  StartGrantUs65PlusAccessOptions,
} from '../../../util/Us65Plus';
import { makeUserFriendlyError } from '../../../util/UserFriendlyError';
import { ActionType } from '../../ActionTypes';
import { store as baseStore } from '../../Store';

const MAX_ATTEMPTS = 3;
const RETRY_DELAY_MS = 1500;

function isAlreadyHasAccessError(error: unknown): boolean {
  return ApiFunctions.isFunctionsError(error) && String(error.code).endsWith('already-exists');
}

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/** Grant US 65+ free access via RevenueCat when eligibility gates pass. */
export async function grantUs65PlusAccess(
  options: StartGrantUs65PlusAccessOptions = {},
  store = baseStore
): Promise<void> {
  const state = store.getState();
  const us65Plus = state.us65Plus;

  if (
    !shouldStartGrantUs65PlusAccess(state, options) ||
    us65Plus.granted === true ||
    us65Plus.loading
  ) {
    return;
  }

  store.dispatch({ type: ActionType.GRANT_US_65_PLUS_ACCESS_LOADING });

  for (let attempt = 1; attempt <= MAX_ATTEMPTS; attempt += 1) {
    try {
      await new ApiClient().grantUsOver65yoAccess(stripUndefined(getUtmParams()));
      store.dispatch({ type: ActionType.GRANT_US_65_PLUS_ACCESS_SUCCESS });
      return;
    } catch (error) {
      if (isAlreadyHasAccessError(error)) {
        store.dispatch({ type: ActionType.GRANT_US_65_PLUS_ACCESS_SUCCESS });
        return;
      }

      recordError(error);

      if (attempt < MAX_ATTEMPTS) {
        await delay(RETRY_DELAY_MS);
      }
    }
  }

  const error = makeUserFriendlyError(
    new Error('Failed to grant US 65+ access'),
    'We could not finish setting up your access. Please try again or contact support if the problem continues.'
  );
  store.dispatch({ type: ActionType.GRANT_US_65_PLUS_ACCESS_FAILURE, error });
}
